import { AppRole } from "@/contexts/AuthContext";

/** Roles that can only view (not manage) all modules */
const VIEW_ONLY_ROLES: AppRole[] = ["principal", "deputy_principal", "hod_cmt", "hod_pst", "hod_sw", "auditor"];

/** Maps a module key to the department head(s) that can manage it */
const MODULE_MANAGERS: Record<string, AppRole[]> = {
  admission: ["superadmin", "head_admission"],
  administration: ["superadmin", "head_administration"],
  academic: ["superadmin", "head_academic", "it_incharge"],
  examination: ["superadmin", "head_examination", "it_incharge"],
  accounting: ["superadmin", "head_accounting"],
  student_services: ["superadmin", "head_student_service"],
  discipline: ["superadmin", "head_discipline"],
  attendance: ["superadmin", "head_administration", "head_academic", "security_man", "hod_cmt", "hod_pst", "hod_sw", "cr_cmt4", "cr_cmt5", "cr_cmt6", "cr_pst4", "cr_pst5", "cr_pst6", "cr_sw4", "cr_sw5", "cr_sw6"],
  user_management: ["superadmin"],
};

/**
 * Whether the user can create / edit / delete records in a given module.
 * Superadmin can always manage. Principal, Deputy & Auditor can only view.
 * Department heads can manage their own module only.
 */
export function canManageModule(roles: AppRole[], module: keyof typeof MODULE_MANAGERS): boolean {
  if (roles.includes("superadmin")) return true;
  const managers = MODULE_MANAGERS[module] ?? [];
  // If the user holds a manager role for this module, allow even if they
  // are otherwise classified as view-only on other modules.
  if (roles.some((r) => managers.includes(r))) return true;
  // View-only roles (Principal, Deputy, Auditor, etc.) cannot manage anything else
  if (roles.every((r) => VIEW_ONLY_ROLES.includes(r) || r === "student")) return false;
  return false;
}

/** Whether the user is view-only (Principal, Deputy Principal, Auditor with no other management role) */
export function isViewOnly(roles: AppRole[]): boolean {
  if (roles.includes("superadmin")) return false;
  return roles.length > 0 && roles.every((r) => VIEW_ONLY_ROLES.includes(r));
}
